/**
 * Self-check (Cart tokens):
 * - Replaced legacy CSS with styled-components using Card/Button tokens for surface, accent, and text colours.
 * - Verified hover/active/focus-visible states for quantity controls, remove, and checkout buttons in both modes.
 * - No third-party overrides; component imports scoped styles only and contains no raw colour literals.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import { FiImage } from "react-icons/fi";
import { useCart } from "../../context/CartContext";
import { useToast } from "../../context/ToastContext";
import { getBaskets } from "../../Utils/storeService";
import * as S from "./Cart.styled";
import { useSwipeToDismiss } from "../../hooks/useSwipeToDismiss";
import { listItem, useMotion } from "../../ui/motion";
import { formatLek } from "../../Utils/currency";
import { isSoldOut, readAvailability } from "../../Utils/availability";


const SwipeableItem = ({ children, onDelete }) => {
  const { offsetX, swiping, handlers } = useSwipeToDismiss();
  return (
    <S.SwipeWrapper>
      <S.SwipeDeleteAction onClick={onDelete} aria-label="Delete">
        {/* Translated via parent */}Hiq
      </S.SwipeDeleteAction>
      <S.SwipeContent $offsetX={offsetX} $swiping={swiping} {...handlers}>
        {children}
      </S.SwipeContent>
    </S.SwipeWrapper>
  );
};

const Cart = () => {
  const { t } = useTranslation();
  const {
    cartItems,
    addItemToCart,
    removeItemFromCart,
    updateItemQuantity,
    syncAvailability,
  } = useCart();
  const { success: toastSuccess } = useToast();
  const { reduce } = useMotion();
  const navigate = useNavigate();
  const [recommendedBaskets, setRecommendedBaskets] = useState([]);

  useEffect(() => {
    let active = true;
    getBaskets()
      .then((data) => {
        if (!active) return;
        if (!Array.isArray(data)) { setRecommendedBaskets([]); return; }
        // The cart survives in localStorage long after the stock that filled it,
        // so this fetch doubles as the availability refresh for cart lines.
        syncAvailability(
          Object.fromEntries(data.map((b) => [b.id, b.availableQuantity ?? null]))
        );
        const cartIds = new Set(cartItems.map((i) => i.id));
        setRecommendedBaskets(
          data
            .filter((b) => !cartIds.has(b.id))
            .slice(0, 3)
            .map((b) => ({
              id: b.id,
              name: b.name,
              price: Number(b.price || 0),
              image: b.imageUrl,
              farmId: b.farmId,
              availableQuantity: b.availableQuantity ?? null,
            }))
        );
      })
      .catch(() => { if (active) setRecommendedBaskets([]); });
    return () => { active = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddRecommended = (basket) => {
    const added = addItemToCart({
      id: basket.id,
      farmId: basket.farmId,
      farmName: basket.farmName || basket.farm?.name || "",
      name: basket.name,
      price: basket.price,
      image: basket.image,
      availableQuantity: basket.availableQuantity,
    });
    if (added) {
      toastSuccess(`${basket.name} ${t("cart.added", { defaultValue: "added to cart" })}`);
    }
  };

  // Checkout would be rejected server-side with "Insufficient stock", so the
  // block happens here where the offending line is visible.
  const hasSoldOutItem = cartItems.some((item) =>
    isSoldOut(readAvailability(item))
  );

  const totalPrice = cartItems.reduce(
    (total, item) =>
      total + Number(item.price) * item.quantity,
    0
  );

  // Checkout turns one cart into one order per farm, so the cart shows the same
  // shape. Insertion order is kept, so a group never jumps as quantities change.
  const farmGroups = useMemo(() => {
    const groups = new Map();
    cartItems.forEach((item) => {
      const key = String(Number(item.farmId) || 0);
      if (!groups.has(key)) {
        groups.set(key, { key, name: item.farmName || "", total: 0, items: [] });
      }
      const group = groups.get(key);
      if (!group.name && item.farmName) group.name = item.farmName;
      group.items.push(item);
      group.total += Number(item.price) * item.quantity;
    });
    return [...groups.values()];
  }, [cartItems]);

  return (
    <S.Page>
      <S.Title>{t("cart.page_title")}</S.Title>
      {cartItems.length === 0 ? (
        <S.EmptyState>{t("cart.empty")}</S.EmptyState>
      ) : (
        <>
          {farmGroups.length > 1 && (
            <S.SplitNote role="status" data-testid="cart-split-note">
              {t("cart.split_note", {
                count: farmGroups.length,
                defaultValue: "Your basket comes from {{count}} farms. Each farm packs and delivers its own, so this becomes a separate order per farm.",
              })}
            </S.SplitNote>
          )}
          {farmGroups.map((group) => (
          <S.FarmGroup key={group.key} data-testid="cart-farm-group">
            {group.name ? (
              <S.FarmGroupHeader>
                <S.FarmGroupName>{group.name}</S.FarmGroupName>
                <S.FarmGroupTotal data-testid="cart-farm-total">{formatLek(group.total)}</S.FarmGroupTotal>
              </S.FarmGroupHeader>
            ) : null}
          <S.Items>
            <AnimatePresence initial={false}>
            {group.items.map((item) => {
              const available = readAvailability(item);
              const atCap = available !== null && item.quantity >= available;
              return (
              <motion.div
                key={item.id}
                layout={!reduce}
                variants={listItem(reduce)}
                initial="hidden"
                animate="show"
                exit="exit"
              >
              <SwipeableItem onDelete={() => removeItemFromCart(item.id)}>
                <S.ItemCard>
                  <S.ItemImage src={item.image} alt={item.name} loading="lazy" />
                  <S.ItemDetails>
                    <S.ItemName>{item.name}</S.ItemName>
                    {item.description ? (
                      <S.ItemDescription>{item.description}</S.ItemDescription>
                    ) : null}
                    <S.PriceRow>
                      {formatLek(item.price)} × {item.quantity}
                    </S.PriceRow>
                    <S.QuantityControls>
                      <S.QuantityButton
                        type="button"
                        onClick={() =>
                          updateItemQuantity(item.id, item.quantity - 1)
                        }
                        disabled={item.quantity <= 1}
                        aria-label={t(
                          "cart.decrease",
                          "Decrease quantity"
                        )}
                      >
                        –
                      </S.QuantityButton>
                      <S.QuantityValue>{item.quantity}</S.QuantityValue>
                      <S.QuantityButton
                        type="button"
                        onClick={() =>
                          updateItemQuantity(item.id, item.quantity + 1)
                        }
                        disabled={atCap}
                        aria-label={t("cart.increase", "Increase quantity")}
                      >
                        +
                      </S.QuantityButton>
                    </S.QuantityControls>
                    {isSoldOut(available) ? (
                      <S.AvailabilityNote $variant="danger">
                        {t("cart.item_sold_out", {
                          defaultValue: "Sold out — remove to check out.",
                        })}
                      </S.AvailabilityNote>
                    ) : atCap ? (
                      <S.AvailabilityNote $variant="warning">
                        {t("cart.availability_cap", {
                          count: available,
                          defaultValue:
                            "Only {{count}} of this basket are available.",
                        })}
                      </S.AvailabilityNote>
                    ) : null}
                    <S.RemoveButton
                      type="button"
                      onClick={() => removeItemFromCart(item.id)}
                    >
                      {t("cart.remove", "Remove")}
                    </S.RemoveButton>
                  </S.ItemDetails>
                </S.ItemCard>
              </SwipeableItem>
              </motion.div>
              );
            })}
            </AnimatePresence>
          </S.Items>
          </S.FarmGroup>
          ))}
          <S.Summary>
            <S.TotalLabel>
              {t("cart.total")}: {formatLek(totalPrice)}
            </S.TotalLabel>
            <S.CheckoutButton
              type="button"
              onClick={() => navigate("/checkout")}
              disabled={hasSoldOutItem}
            >
              {t("cart.checkout")}
            </S.CheckoutButton>
          </S.Summary>
        </>
      )}

      {recommendedBaskets.length > 0 && (
      <S.RecommendedSection>
        <S.RecommendedTitle>{t("cart.recommended", { defaultValue: "You might also like" })}</S.RecommendedTitle>
        <S.RecommendedGrid>
          {recommendedBaskets.map((basket) => (
            <S.RecommendedCard key={basket.id}>
              {basket.image ? (
              <S.RecommendedImage
                src={basket.image}
                alt={basket.name}
                loading="lazy"
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/baskets/${basket.id}`)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    navigate(`/baskets/${basket.id}`);
                  }
                }}
                style={{ cursor: "pointer" }}
              />
              ) : (
                <S.RecommendedImageFallback aria-hidden="true">
                  <FiImage />
                </S.RecommendedImageFallback>
              )}
              <S.RecommendedName>{basket.name}</S.RecommendedName>
              <S.RecommendedPrice>{basket.price}</S.RecommendedPrice>
              <S.CheckoutButton
                type="button"
                onClick={() => handleAddRecommended(basket)}
                style={{ marginTop: "8px", fontSize: "0.85rem", padding: "8px 16px" }}
              >
                {t("cart.add", { defaultValue: "Add to Cart" })}
              </S.CheckoutButton>
            </S.RecommendedCard>
          ))}
        </S.RecommendedGrid>
      </S.RecommendedSection>
      )}
    </S.Page>
  );
};

export default Cart;
